/**
 * What a guest-trial account is told, and where it is sent, when its trial
 * credits run out or it reaches something only a full account may do.
 *
 * A guest cannot buy credits: its trial is a one-time, Fias-paid grant (see
 * docs/guest-trial.md §2), so "add more credits" is a dead end for it. Every
 * surface — the global listener, the inline chat messages, the builder's
 * toasts, the image generator — sends a guest to sign up instead, using these
 * constants so they all say the same thing and land on the same page. Signing
 * up upgrades the same account in place, so everything the guest made stays.
 */
import { useContext } from 'react';
import { AuthContext } from '@/auth/context/AuthContext';

/** Where a guest whose trial credits ran out is sent. */
export const GUEST_TRIAL_ENDED_SIGNUP_PATH = '/auth/signup?reason=trial-ended';
/** Where a guest who reaches a full-account-only action is sent. */
export const GUEST_CREATE_ACCOUNT_SIGNUP_PATH = '/auth/signup?reason=create-account';

/** Headline when a guest's trial credits are used up. */
export const GUEST_TRIAL_ENDED_TITLE = 'Your free trial credits are used up';
/** The call to action that goes with it. */
export const GUEST_TRIAL_ENDED_CTA = 'Create a free account to keep going';

/**
 * Whether the signed-in account is a guest. Reads the auth context directly
 * (not `useAuth`, which throws outside a provider) so the shared credit
 * messages can render anywhere — outside a provider they show the full-account
 * copy.
 */
export function useIsGuestAccount(): boolean {
  return useContext(AuthContext)?.isGuest ?? false;
}

/**
 * True when an error text says the user ran out of credits — the server's
 * `InsufficientCreditsError` ("Insufficient credits: need …"), the gateway's
 * `INSUFFICIENT_CREDITS` code, and the assistant's "Not enough credits …".
 */
export function isInsufficientCreditsText(text: string | null | undefined): boolean {
  if (!text) return false;
  const lower = text.toLowerCase();
  return (
    lower.includes('insufficient credit') ||
    lower.includes('insufficient_credits') ||
    lower.includes('not enough credits')
  );
}
